import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const HERO_SLIDES = [
  {
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=2000&q=80",
    kicker: "Autumn Edit · MMXXVI",
    title: "Considered clothing for the modern minimalist",
    cta: "Shop the edit",
    to: "/shop",
  },
  {
    img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=2000&q=80",
    kicker: "Women · New in",
    title: "Sculpted silhouettes for the season",
    cta: "Shop Women",
    to: "/shop/women",
  },
  {
    img: "https://images.unsplash.com/photo-1617137968427-85924c800a22?auto=format&fit=crop&w=2000&q=80",
    kicker: "Men · Tailoring",
    title: "Suits that move with you",
    cta: "Shop Men",
    to: "/shop/men",
  },
];

const CATEGORY_TILES = [
  { title: "Shop Women", to: "/shop/women", img: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=800&q=80" },
  { title: "Shop Men", to: "/shop/men", img: "https://images.unsplash.com/photo-1617137968427-85924c800a22?auto=format&fit=crop&w=800&q=80" },
  { title: "Accessories", to: "/shop/accessories", img: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?auto=format&fit=crop&w=800&q=80" },
  { title: "New Arrivals", to: "/shop", img: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=800&q=80" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const featured = products.filter((p) => p.featured);
  const women = products.filter((p) => p.category === "women");
  const men = products.filter((p) => p.category === "men");
  const accessories = products.filter((p) => p.category === "accessories");
  const deals = products.filter((p) => Number(p.discount_percent || 0) > 0);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[420px] md:h-[560px] overflow-hidden bg-[#1a1d29]">
          {HERO_SLIDES.map((s, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-700 ${i === slide ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            >
              <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
              <div className="absolute inset-0 max-w-[1500px] mx-auto px-6 md:px-10 flex items-center">
                <div className="text-white max-w-2xl">
                  <div className="text-xs font-medium uppercase tracking-[0.24em] text-[#e0a500]">{s.kicker}</div>
                  <h2 className="mt-4 font-display text-4xl md:text-6xl font-semibold leading-[1.05]">{s.title}</h2>
                  <Link
                    to={s.to}
                    className="btn-primary inline-flex items-center gap-2 mt-8 text-sm"
                    data-testid={`hero-cta-${i}`}
                  >
                    {s.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={() => setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white w-11 h-11 rounded-full flex items-center justify-center shadow"
            data-testid="hero-prev"
            aria-label="prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSlide((s) => (s + 1) % HERO_SLIDES.length)}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white w-11 h-11 rounded-full flex items-center justify-center shadow"
            data-testid="hero-next"
            aria-label="next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "bg-white w-8" : "bg-white/50 w-4"}`}
                aria-label={`slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Category tiles */}
      <section className="max-w-[1500px] mx-auto px-3 md:px-6 -mt-24 md:-mt-32 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {CATEGORY_TILES.map((c) => (
            <Link
              key={c.title}
              to={c.to}
              className="card p-3 md:p-4 flex flex-col hover:shadow-lg transition-shadow"
              data-testid={`category-tile-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-display text-lg md:text-xl font-semibold text-[#1a1d29] mb-3">{c.title}</div>
              <div className="aspect-[4/3] overflow-hidden bg-[#faf7f2] rounded-md">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover" />
              </div>
              <div className="mt-3 ec-link text-sm">Shop now →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Coupon banner */}
      <section className="max-w-[1500px] mx-auto px-3 md:px-6 mt-8">
        <div className="rounded-md bg-[#1a1d29] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#e0a500] font-semibold">Studio offer</div>
            <div className="font-display text-2xl md:text-3xl font-semibold mt-2">
              Get 10% off your first order · use <span className="text-[#c8532e]">WELCOME10</span>
            </div>
          </div>
          <Link to="/shop" className="btn-primary text-sm shrink-0">Shop now</Link>
        </div>
      </section>

      {deals.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-3 md:px-6 mt-8">
          <div className="card p-4 md:p-6">
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-xs font-semibold text-[#c8532e] uppercase tracking-[0.2em]">Limited time</div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1a1d29] mt-2">Today's Deals</h3>
              </div>
              <Link to="/shop" className="ec-link text-sm hidden md:inline" data-testid="deals-see-all">See all deals →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {deals.slice(0, 6).map((p) => {
                const final = Number(p.price) * (1 - Number(p.discount_percent) / 100);
                return (
                  <Link to={`/product/${p.id}`} key={p.id} className="group" data-testid={`deal-card-${p.id}`}>
                    <div className="aspect-square bg-[#faf7f2] rounded-md overflow-hidden border border-[#e0d8c8]">
                      {p.images?.[0] && (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="deal-tag">-{Math.round(p.discount_percent)}%</span>
                      <span className="text-[#c8532e] font-bold">{formatINR(final)}</span>
                    </div>
                    <div className="mt-1 text-xs line-clamp-2 text-[#1a1d29]">{p.name}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {featured.length > 0 && <ProductRow title="Featured for you" products={featured} testId="row-featured" />}
      {women.length > 0 && <ProductRow title="Best sellers · Women" products={women} testId="row-women" />}
      {men.length > 0 && <ProductRow title="New in · Men" products={men} testId="row-men" />}
      {accessories.length > 0 && <ProductRow title="Complete the look" products={accessories} testId="row-accessories" />}

      <section className="max-w-[1500px] mx-auto px-3 md:px-6 mt-8">
        <div className="card p-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            ["Free Shipping", "On orders over ₹5,000"],
            ["Secure Payments", "Razorpay · UPI · Cards"],
            ["Easy Returns", "14-day return window"],
            ["Considered", "Ethically made pieces"],
          ].map(([t, s]) => (
            <div key={t}>
              <div className="font-display text-lg font-semibold text-[#1a1d29]">{t}</div>
              <div className="text-xs text-[#6b6b6b] mt-1">{s}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ProductRow({ title, products, testId }) {
  return (
    <section className="max-w-[1500px] mx-auto px-3 md:px-6 mt-8" data-testid={testId}>
      <div className="card p-4 md:p-6">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1a1d29]">{title}</h3>
          <Link to="/shop" className="ec-link text-sm hidden md:inline">See more →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {products.slice(0, 5).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
